import { useMutationApi, useQueryApi } from 'utils/api'

import type { CustomError } from 'types/custom-error'
import type {
  ParamsGetPegawaiPeriodikList,
  ParamsGetPegawaiPeriodikDetail,
  ParamsBulkUpdatePegawaiPeriodikRecommendation,
  ParamsBulkSendPegawaiPeriodikRecommendationToDinas,
  ParamsGetPeriodikRecommendationList,
  ParamsGetPegawaiPeriodikRecommendationList,
  ParamsGetPegawaiPeriodikRecommendationDetail,
  ResponseGetPeriodikRecommendationList,
  ResponseGetPegawaiPeriodikRecommendationList,
  ResponseGetPegawaiPeriodikRecommendationDetail,
  ResponseGetPeriodikList,
  ParamsGetPeriodikList,
  ResponseGetPegawaiPeriodikList,
  ResponseGetPegawaiPeriodikDetail,
  ParamsBulkSendPeriodik,
  ParamsBulkPenilaianPeriodik,
} from 'types/kinerja-asn-swasta'
import type { UseMutationOptions, UseQueryOptions } from 'react-query'

const basePath = '/guru/skp'

// -------------------------------------
// TK: Get periodik list
// -------------------------------------
export const API_ASN_SWASTA_GET_PERIODIK_RECOMMENDATION_LIST_QUERY_KEY =
  'API_ASN_SWASTA_GET_PERIODIK_RECOMMENDATION_LIST_QUERY_KEY'

export function useApiGetPeriodikRecommendationList({
  params,
  options,
}: {
  params: ParamsGetPeriodikRecommendationList
  options?: UseQueryOptions<ResponseGetPeriodikRecommendationList>
}) {
  const { skpYear, ...restParams } = params

  return useQueryApi<ResponseGetPeriodikRecommendationList>(
    `${basePath}/v1/penilaian/recommendation/periodik`,
    {
      axiosConfig: {
        params: {
          skp_year: skpYear,
          ...restParams,
        },
      },
      queryConfig: {
        ...options,
      },
      queryKey: [
        API_ASN_SWASTA_GET_PERIODIK_RECOMMENDATION_LIST_QUERY_KEY,
        { skpYear, npsn: params.npsn, tipe: params.tipe },
      ],
    }
  )
}

// -------------------------------------
// Get pegawai recommendation list
// -------------------------------------
export const API_ASN_SWASTA_GET_PEGAWAI_PERIODIK_RECOMMENDATION_LIST_QUERY_KEY =
  'API_ASN_SWASTA_GET_PEGAWAI_PERIODIK_RECOMMENDATION_LIST_QUERY_KEY'

export function useApiGetPegawaiPeriodikRecommendationList({
  params,
  options,
}: {
  params: ParamsGetPegawaiPeriodikRecommendationList
  options?: UseQueryOptions<ResponseGetPegawaiPeriodikRecommendationList>
}) {
  const { skpYear, skpMonth, npsn } = params

  return useQueryApi<ResponseGetPegawaiPeriodikRecommendationList>(
    `${basePath}/v1/penilaian/recommendation/periodik/detail`,
    {
      axiosConfig: {
        params: {
          skp_year: skpYear,
          skp_month: skpMonth,
          npsn,
        },
      },
      queryConfig: {
        ...options,
      },
      queryKey: [
        API_ASN_SWASTA_GET_PEGAWAI_PERIODIK_RECOMMENDATION_LIST_QUERY_KEY,
        { skpYear, skpMonth, npsn },
      ],
    }
  )
}

// -------------------------------------
// Get pegawai periodik recommendation detail
// -------------------------------------
export const API_ASN_SWASTA_GET_PEGAWAI_PERIODIK_RECOMMENDATION_DETAIL_QUERY_KEY =
  'API_ASN_SWASTA_GET_PEGAWAI_PERIODIK_RECOMMENDATION_DETAIL_QUERY_KEY'

export function useApiGetRecommendationPeriodikPegawaiDetail({
  params,
  options,
}: {
  params: ParamsGetPegawaiPeriodikRecommendationDetail
  options?: UseQueryOptions<ResponseGetPegawaiPeriodikRecommendationDetail>
}) {
  const { skpYear, skpMonth, ptkId } = params

  return useQueryApi<ResponseGetPegawaiPeriodikRecommendationDetail>(
    `${basePath}/v1/penilaian/recommendation/periodik/detail/${ptkId}`,
    {
      axiosConfig: {
        params: {
          skp_year: skpYear,
          skp_month: skpMonth,
        },
      },
      queryConfig: {
        ...options,
      },
      queryKey: [
        API_ASN_SWASTA_GET_PEGAWAI_PERIODIK_RECOMMENDATION_DETAIL_QUERY_KEY,
        { skpYear, skpMonth, ptkId },
      ],
    }
  )
}

// -------------------------------------
// Bulk recommendation periodik
// -------------------------------------
export function useApiBulkUpdatePegawaiPeriodikRecommendation(
  opts?: UseMutationOptions<
    unknown,
    CustomError,
    ParamsBulkUpdatePegawaiPeriodikRecommendation
  >
) {
  return useMutationApi<
    unknown,
    CustomError,
    ParamsBulkUpdatePegawaiPeriodikRecommendation
  >(`${basePath}/v1/penilaian/recommendation/periodik/bulk`, {
    axiosConfig: {
      method: 'POST',
    },
    mutationConfig: {
      ...opts,
    },
  })
}

// -------------------------------------
// Bulk send recommendation periodik to dinas
// -------------------------------------
export function useApiBulkSendPegawaiPeriodikRecommendationToDinas(
  opts?: UseMutationOptions<
    unknown,
    CustomError,
    ParamsBulkSendPegawaiPeriodikRecommendationToDinas
  >
) {
  return useMutationApi<
    unknown,
    CustomError,
    ParamsBulkSendPegawaiPeriodikRecommendationToDinas
  >(`${basePath}/v1/penilaian/recommendation/periodik/send-dinas/bulk`, {
    axiosConfig: {
      method: 'POST',
    },
    mutationConfig: {
      ...opts,
    },
  })
}

// -------------------------------------
// PPK: Get periodik list
// -------------------------------------
export const API_ASN_SWASTA_GET_PERIODIK_LIST_QUERY_KEY =
  'API_ASN_SWASTA_GET_PERIODIK_LIST_QUERY_KEY'

export function useApiGetPeriodikList({
  params,
  options,
}: {
  params: ParamsGetPeriodikList
  options?: UseQueryOptions<ResponseGetPeriodikList>
}) {
  const { skpYear, ...restParams } = params

  return useQueryApi<ResponseGetPeriodikList>(
    `${basePath}/v1/ks/dinas/swasta/penilaian/periodik`,
    {
      axiosConfig: {
        params: {
          skp_year: skpYear,
          ...restParams,
        },
      },
      queryConfig: {
        ...options,
      },
      queryKey: [
        API_ASN_SWASTA_GET_PERIODIK_LIST_QUERY_KEY,
        { skpYear, tipe: params.tipe },
      ],
    }
  )
}

// -------------------------------------
// PPK: Get pegawai periodik list
// -------------------------------------
export const API_ASN_SWASTA_GET_PEGAWAI_PERIODIK_LIST_QUERY_KEY =
  'API_ASN_SWASTA_GET_PEGAWAI_PERIODIK_LIST_QUERY_KEY'

export function useApiGetPegawaiPeriodikList({
  params,
  options,
}: {
  params: ParamsGetPegawaiPeriodikList
  options?: UseQueryOptions<ResponseGetPegawaiPeriodikList>
}) {
  const { skpYear, skpMonth, tipe } = params

  return useQueryApi<ResponseGetPegawaiPeriodikList>(
    `${basePath}/v1/ks/dinas/swasta/penilaian/periodik/detail`,
    {
      axiosConfig: {
        params: {
          skp_year: skpYear,
          skp_month: skpMonth,
          tipe,
        },
      },
      queryConfig: {
        ...options,
      },
      queryKey: [
        API_ASN_SWASTA_GET_PEGAWAI_PERIODIK_LIST_QUERY_KEY,
        { skpYear, skpMonth, tipe },
      ],
    }
  )
}

// -------------------------------------
// PPK: Get pegawai periodik detail
// -------------------------------------
export const API_ASN_SWASTA_GET_PEGAWAI_PERIODIK_DETAIL_QUERY_KEY =
  'API_ASN_SWASTA_GET_PEGAWAI_PERIODIK_DETAIL_QUERY_KEY'

export function useApiGetPegawaiPeriodikDetail({
  params,
  options,
}: {
  params: ParamsGetPegawaiPeriodikDetail
  options?: UseQueryOptions<ResponseGetPegawaiPeriodikDetail>
}) {
  const { skpYear, skpMonth, ptkId, tipe } = params

  return useQueryApi<ResponseGetPegawaiPeriodikDetail>(
    `${basePath}/v1/ks/dinas/swasta/penilaian/periodik/detail/${ptkId}`,
    {
      axiosConfig: {
        params: {
          skp_year: skpYear,
          skp_month: skpMonth,
          tipe,
        },
      },
      queryConfig: {
        ...options,
      },
      queryKey: [
        API_ASN_SWASTA_GET_PEGAWAI_PERIODIK_DETAIL_QUERY_KEY,
        { skpYear, skpMonth, ptkId, tipe },
      ],
    }
  )
}

// -------------------------------------
// PPK: Bulk penilaian periodik pegawai
// -------------------------------------
export function useApiBulkPenilaianPeriodikPegawai(
  opts?: UseMutationOptions<unknown, CustomError, ParamsBulkPenilaianPeriodik>
) {
  return useMutationApi<unknown, CustomError, ParamsBulkPenilaianPeriodik>(
    `${basePath}/v1/ks/dinas/swasta/penilaian/periodik/bulk`,
    {
      axiosConfig: {
        method: 'POST',
      },
      mutationConfig: {
        ...opts,
      },
    }
  )
}

// -------------------------------------
// PPK: Bulk send penilaian periodik to BKN
// -------------------------------------
export function useApiBulkSendPeriodikToBkn(
  opts?: UseMutationOptions<unknown, CustomError, ParamsBulkSendPeriodik>
) {
  return useMutationApi<unknown, CustomError, ParamsBulkSendPeriodik>(
    `${basePath}/v1/ks/dinas/swasta/penilaian/periodik/send-bkn/bulk`,
    {
      axiosConfig: {
        method: 'POST',
      },
      mutationConfig: {
        ...opts,
      },
    }
  )
}
